import React, { FC, useEffect, useState } from 'react';

import { useApp } from 'App';
import { AttachmentResponse } from 'utils/api/inbox';
import { ResponseError } from 'utils/api';
import { notificationTypes } from 'utils/constants';
import { ActionTypes } from 'App/types';
import usePrefix from 'utils/usePrefix';
import { getIconFromMimeType } from 'utils/mimeType';

import Icon from 'components/Icons';
import Button from 'components/Button';
import ConfirmModal from 'components/ConfirmModal';
import AttachmentsPreviewModal from 'components/AttachmentsPreviewModal';

import { filterAfterDelete, hasServerResource } from './deleteFilter';

import {
  AttachmentsStyled,
  AttachmentsListStyled,
  AttachmentItemStyled,
  AttachmentNameStyled,
  AttachmentItemActionsStyled,
  AttachmentTypeStyled,
  AttachmentsHeaderStyled,
  AttachmentsActionsStyled,
  UploadProgressItemStyled,
  ProgressLabel,
  ProgressTrack,
  ProgressFill,
  ProgressCancelButton,
} from './styles';

interface NoticesProps {
  attachments: AttachmentResponse[];
  uploadProgress?: number | null;
  onCancelUpload?: () => void;
  noticeId?: number;
  onChange?: (attachments: AttachmentResponse[]) => void;
  downloadAttachment: (noticeId: number, attachmentId: number) => Promise<Blob>;
  downloadAttachmentsZip?: (noticeId: number) => Promise<Blob>;
  deleteAttachment?: (noticeId: number, attachmentId: number) => Promise<void>;
  canEdit?: boolean;
}

const Attachments: FC<NoticesProps> = ({
  attachments,
  uploadProgress,
  onCancelUpload,
  noticeId,
  onChange,
  downloadAttachment,
  downloadAttachmentsZip,
  deleteAttachment,
  canEdit,
}) => {
  const t = usePrefix('Notices');

  const [, dispatch] = useApp();
  const [orderedAttachments, setOrderedAttachments] = useState<AttachmentResponse[]>(attachments);
  const [pendingDelete, setPendingDelete] = useState<AttachmentResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<AttachmentResponse | null>(null);

  useEffect(() => {
    setOrderedAttachments(attachments);
  }, [attachments]);

  const applyChange = (next: AttachmentResponse[]) => {
    setOrderedAttachments(next);
    onChange?.(next);
  };

  const swap = (index: number, direction: 'up' | 'down') => {
    const j = direction === 'up' ? index - 1 : index + 1;
    if (j < 0 || j >= orderedAttachments.length) return;
    const next = [...orderedAttachments];
    [next[index], next[j]] = [next[j], next[index]];
    applyChange(next);
  };

  const onDeleteRequest = (att: AttachmentResponse) => {
    setPendingDelete(att);
  };

  const onDeleteCancel = () => {
    setPendingDelete(null);
  };

  const onDeleteConfirm = async () => {
    if (!pendingDelete) return;
    if (!hasServerResource(noticeId, pendingDelete.id, deleteAttachment)) {
      applyChange(filterAfterDelete(orderedAttachments, pendingDelete, false));
      setPendingDelete(null);
      return;
    }
    setIsDeleting(true);
    try {
      await deleteAttachment!(noticeId!, pendingDelete.id!);
      applyChange(filterAfterDelete(orderedAttachments, pendingDelete, true));
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { type: notificationTypes.success },
      });
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
    } finally {
      setIsDeleting(false);
      setPendingDelete(null);
    }
  };

  const onDownload = async (att: AttachmentResponse) => {
    if (!noticeId || !att.id) {
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { type: notificationTypes.error },
      });
      return;
    }
    try {
      const blob = await downloadAttachment(noticeId, att.id);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = att.name ?? att.uri.split('/').pop() ?? 'file';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
    }
  };

  const onDownloadAll = async () => {
    if (!downloadAttachmentsZip || !noticeId) {
      await Promise.allSettled(orderedAttachments.map((att) => onDownload(att)));
      return;
    }
    try {
      const blob = await downloadAttachmentsZip(noticeId);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `attachments-${noticeId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
    }
  };

  const onPreview = (att?: AttachmentResponse) => {
    setPreviewAttachment(att ?? orderedAttachments[0] ?? null);
  };

  return (
    <AttachmentsStyled>
      <AttachmentsHeaderStyled>
        <h3>{t('attachments')}</h3>
        <AttachmentsActionsStyled>
          <Button label={t('cta_preview')} color="link" onClick={() => onPreview()} noPadding />
          <Button label={t('cta_download_all')} color="link" onClick={onDownloadAll} noPadding />
        </AttachmentsActionsStyled>
      </AttachmentsHeaderStyled>
      <AttachmentsListStyled>
        {orderedAttachments.map((att, index) => (
          <AttachmentItemStyled key={att.uri}>
            <AttachmentTypeStyled>
              <Icon
                type={getIconFromMimeType(att.mimeType)}
                width="24"
                height="24"
                color="#3e3e3e"
              />
            </AttachmentTypeStyled>
            <AttachmentNameStyled>{att.name}</AttachmentNameStyled>
            <AttachmentItemActionsStyled>
              {canEdit ? (
                <>
                  <Button
                    label={t('cta_move_down')}
                    color="secondary"
                    disabled={index === orderedAttachments.length - 1 || isDeleting}
                    onClick={() => swap(index, 'down')}
                  />
                  <Button
                    label={t('cta_move_up')}
                    color="secondary"
                    disabled={index === 0 || isDeleting}
                    onClick={() => swap(index, 'up')}
                  />
                </>
              ) : null}
              <Button
                label={t('cta_download')}
                color="link"
                onClick={() => onDownload(att)}
                disabled={isDeleting}
                noPadding
              />
              <Button
                label={t('cta_preview')}
                color="link"
                onClick={() => onPreview(att)}
                disabled={isDeleting}
                noPadding
              />
              {canEdit ? (
                <Button
                  label={t('cta_delete')}
                  color="red"
                  onClick={() => onDeleteRequest(att)}
                  disabled={isDeleting}
                  noPadding
                />
              ) : null}
            </AttachmentItemActionsStyled>
          </AttachmentItemStyled>
        ))}
      </AttachmentsListStyled>

      {uploadProgress !== null && uploadProgress !== undefined ? (
        <UploadProgressItemStyled>
          <ProgressLabel>
            <span>{t('uploading')}</span>
            <span>{uploadProgress}%</span>
          </ProgressLabel>
          <ProgressTrack>
            <ProgressFill value={uploadProgress} />
          </ProgressTrack>
          {onCancelUpload ? (
            <ProgressCancelButton type="button" onClick={onCancelUpload}>
              {t('cancel_upload')}
            </ProgressCancelButton>
          ) : null}
        </UploadProgressItemStyled>
      ) : null}

      {!!pendingDelete && (
        <ConfirmModal
          title={t('delete_attachment_title')}
          text={t('delete_attachment_confirm', { name: pendingDelete.name ?? pendingDelete.uri })}
          onCancel={onDeleteCancel}
          onConfirm={onDeleteConfirm}
        />
      )}

      {!!previewAttachment && (
        <AttachmentsPreviewModal
          attachments={orderedAttachments}
          initialAttachment={previewAttachment}
          noticeId={noticeId}
          onClose={() => setPreviewAttachment(null)}
          onDownload={onDownload}
          downloadAttachment={downloadAttachment}
        />
      )}
    </AttachmentsStyled>
  );
};

export default Attachments;
